import './playlists.system-administrator.screen.scss';
import {Box, Button, Stack} from '@mui/material';
import {useState, MutableRefObject, useRef, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {PlaylistType} from '../../../../../../backend/enums/playlist-type';
import {Playlist} from '../../../../../../backend/interfaces/playlist';
import {usePaginatedTable} from '../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../repositories/playlists';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {AlertModal} from '../../../../../../components/alert-modal/alert-modal';
import AddIcon from '@mui/icons-material/Add';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';
import {PlaylistsMenu} from '../../../../../../components/user-playlists/playlists-menu';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
}

export default function PlaylistsSystemAdministratorOfficeScreen() {
  const [playlistType, setPlaylistType] = useState<PlaylistType>(PlaylistType.Karaoke);
  const playlistTypeRef: MutableRefObject<PlaylistType> = useRef(PlaylistType.Karaoke);
  const [deletedPlaylistName, setDeletedPlaylistName] = useState<string | null>(
    new URLSearchParams(useLocation().search).get(ScreenPaths.PlaylistDeletedQuery)
  );
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url, path} = useRouteMatch();
  let history = useHistory();

  // Fetch playlists
  const handleGetPlaylists = async (limit: number, skip: number, search?: string) => {
    // Load playlists
    const result: {totalCount: number; data: Array<Playlist>} =
      await PlaylistsRepository.instance.getPlaylistsAsSystemAdministrator(
        skip,
        limit,
        playlistTypeRef.current,
        search,
        true
      );

    // Process data
    const processedData: PaginatedTableData<Playlist> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        avatarUrl: obj.imageUrl,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            // The name is localized
            value: obj.name || '',
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
  } = usePaginatedTable<Playlist>({
    fetchData: handleGetPlaylists,
  });

  // Change the playlist type
  const handleChangePlaylistType = (value: string) => {
    if (value !== playlistTypeRef.current) {
      // Update the playlist type
      setPlaylistType(value as PlaylistType);
    }
  };

  // Fetch the playlists again when the type changes
  useEffect(() => {
    if (playlistType !== playlistTypeRef.current) {
      // Update the playlist type
      playlistTypeRef.current = playlistType;

      // Fetch again
      handleApplyFilters(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistType]);

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedPlaylistName(null);

    // Reset history
    history.replace(path);
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        pageTitle={'Listas de reprodução'}
        isLoading={false}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.user_playlists.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
            <PlaylistsMenu
              playlistType={playlistType}
              onChangePlaylistType={handleChangePlaylistType}
            />
          </Stack>
        }
        rightColumn={
          <Box display="flex" flexDirection="row" gap="20px">
            <Link
              className="devices-system-administrator-screen-button-link"
              to={url.charAt(url.length - 1) === '/' ? `${url}create` : `${url}/create`}
            >
              <Button variant="contained" color="primary" endIcon={<AddIcon />}>
                {t(translations.playlists.system_administrator.root.add_button)}
              </Button>
            </Link>
          </Box>
        }
      />
      <Box className="playlists-listing-container">
        <Box className="playlists-listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'other',
            }}
            emptyTableMessage={t(translations.playlists.system_administrator.root.no_playlists)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.playlists.system_administrator.root.name_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
      <AlertModal
        id="playlist-deleted-warning"
        opened={deletedPlaylistName ? true : false}
        title={t(translations.playlists.system_administrator.root.deleted_playlist_dialog_title, {
          name: deletedPlaylistName,
        })}
        content={t(
          translations.playlists.system_administrator.root.deleted_playlist_dialog_message
        )}
        handleClose={handleCloseDialog}
        actions={[
          {
            label: t(
              translations.playlists.system_administrator.root.deleted_playlist_dialog_ok_button
            ),
            type: 'neutral',
            handleClick: handleCloseDialog,
          },
        ]}
      />
    </Box>
  );
}
